/**
 * Parse pros/cons with option labels into table format
 * Input: ["**Option A:** pro text", "**Option B:** pro text"]
 * Output: { optionA: {name, items}, optionB: {name, items} }
 */
export const parseLogicData = (pros, cons) => {
  const result = {
    optionA: { name: '', items: [] },
    optionB: { name: '', items: [] }
  };

  // Track which names we've seen to assign to A or B consistently
  const nameToColumn = new Map();
  let columnIndex = 0;

  // Parse pros
  pros.forEach(pro => {
    const match = pro.match(/\*\*([^:]+):\*\*\s*(.+)/);
    if (match) {
      const [, optionName, text] = match;
      const cleanName = optionName.trim();
      let cleanText = text.trim();
      
      // Determine which column this name belongs to
      if (!nameToColumn.has(cleanName)) {
        nameToColumn.set(cleanName, columnIndex === 0 ? 'A' : 'B');
        columnIndex++;
      }
      
      const column = nameToColumn.get(cleanName);
      
      // Keep text as-is (don't split on commas within sentences)
      const items = [cleanText];
      
      items.forEach(itemText => {
        if (column === 'A') {
          if (!result.optionA.name) result.optionA.name = cleanName;
          result.optionA.items.push({ type: 'pro', text: itemText });
        } else {
          if (!result.optionB.name) result.optionB.name = cleanName;
          result.optionB.items.push({ type: 'pro', text: itemText });
        }
      });
    } else {
      // No label, add to optionA by default
      result.optionA.items.push({ type: 'pro', text: pro });
    }
  });

  // Parse cons
  cons.forEach(con => {
    const match = con.match(/\*\*([^:]+):\*\*\s*(.+)/);
    if (match) {
      const [, optionName, text] = match;
      const cleanName = optionName.trim();
      let cleanText = text.trim();
      
      // Determine which column this name belongs to
      if (!nameToColumn.has(cleanName)) {
        nameToColumn.set(cleanName, columnIndex === 0 ? 'A' : 'B');
        columnIndex++;
      }
      
      const column = nameToColumn.get(cleanName);
      
      // Keep text as-is (don't split on commas within sentences)
      const items = [cleanText];
      
      items.forEach(itemText => {
        if (column === 'A') {
          result.optionA.items.push({ type: 'con', text: itemText });
        } else {
          result.optionB.items.push({ type: 'con', text: itemText });
        }
      });
    } else {
      // No label
      result.optionA.items.push({ type: 'con', text: con });
    }
  });

  // Fallback to default names if no names found
  if (!result.optionA.name) result.optionA.name = 'Option A';
  if (!result.optionB.name) result.optionB.name = 'Option B';

  return result;
};

/**
 * Parse vibe text with option labels - splits by ✓/✗ markers
 * Input: "**Option A:** ✓ feeling 1 | ✓ feeling 2 | **Option B:** ✗ feeling"
 * Output: { optionA: {...}, optionB: {...} }
 */
export const parseVibeData = (vibeText) => {
  const result = {
    optionA: { name: '', items: [] },
    optionB: { name: '', items: [] }
  };

  if (!vibeText) return result;

  const parts = vibeText.split('|').map(p => p.replace(/\|/g, '').trim());
  
  parts.forEach((part, index) => {
    const match = part.trim().match(/\*\*([^:]+):\*\*\s*(.+)/);
    if (match) {
      const [, optionName, feelingsText] = match;
      const cleanName = optionName.trim();
      
      // Split by ✓ and ✗ markers to get individual feelings
      const feelingItems = [];
      let currentFeeling = feelingsText.trim();
      
      // Split by both ✓ and ✗ markers
      const splitByMarkers = currentFeeling.split(/(✓|✗)/).filter(s => s.trim());
      
      let currentMarker = null;
      let currentText = '';
      
      for (let i = 0; i < splitByMarkers.length; i++) {
        const segment = splitByMarkers[i].trim();
        
        if (segment === '✓' || segment === '✗') {
          // Save previous feeling if exists
          if (currentMarker && currentText) {
            feelingItems.push({
              marker: currentMarker,
              text: currentText.replace(/^[,|]\s*/, '').trim()
            });
          }
          currentMarker = segment;
          currentText = '';
        } else {
          currentText += segment;
        }
      }
      
      // Add the last feeling
      if (currentMarker && currentText) {
        feelingItems.push({
          marker: currentMarker,
          text: currentText.replace(/^[,|]\s*/, '').trim()
        });
      }
      
      // If no markers found, treat as single item with keyword detection
      if (feelingItems.length === 0 && currentFeeling) {
        const feelingLower = currentFeeling.toLowerCase();
        
        // Skip placeholder text
        const isPlaceholder = feelingLower.includes('(no ') || 
                             feelingLower.includes('described)') ||
                             feelingLower.includes('mentioned)');
        
        if (!isPlaceholder) {
          const isContraction = feelingLower.includes('contract') ||
                               feelingLower.includes('heavy') ||
                               feelingLower.includes('dread') ||
                               feelingLower.includes('nervous') ||
                               feelingLower.includes('worry') ||
                               feelingLower.includes('resistance');
          
          const isExpansion = feelingLower.includes('expansion') ||
                             feelingLower.includes('excitement') ||
                             feelingLower.includes('light') ||
                             feelingLower.includes('yes') ||
                             feelingLower.includes('relief') ||
                             feelingLower.includes('open');
          
          feelingItems.push({
            marker: isExpansion ? '✓' : (isContraction ? '✗' : '✓'),
            text: currentFeeling
          });
        } else {
          // For placeholders, add without marker
          feelingItems.push({
            marker: null,
            text: currentFeeling
          });
        }
      }
      
      // Convert to items with type
      const items = feelingItems.map(f => ({
        type: f.marker === '✓' ? 'positive' : (f.marker === '✗' ? 'negative' : 'neutral'),
        text: f.text
      }));
      
      // First option goes to A, second to B
      if (index === 0) {
        result.optionA.name = cleanName;
        result.optionA.items = items;
      } else {
        result.optionB.name = cleanName;
        result.optionB.items = items;
      }
    }
  });

  // Fallback to default names if no names found
  if (!result.optionA.name) result.optionA.name = 'Option A';
  if (!result.optionB.name) result.optionB.name = 'Option B';

  return result;
};

/**
 * Parse universe text with option labels - splits by ✓/✗ markers
 * Input: "**Signs toward A:** ✓ sign 1 | ✓ sign 2 | **Option B:** ✗ sign 1"
 */
export const parseUniverseData = (universeText) => {
  const result = {
    optionA: { name: '', items: [] },
    optionB: { name: '', items: [] }
  };

  if (!universeText) return result;

  // Try to split by double asterisks pattern
  const pattern = /\*\*([^:]+):\*\*/g;
  const matches = [...universeText.matchAll(pattern)];
  
  if (matches.length === 0) return result;
  
  matches.forEach((match, index) => {
    const optionName = match[1].trim();
    const startPos = match.index + match[0].length;
    const endPos = matches[index + 1] ? matches[index + 1].index : universeText.length;
    const signsText = universeText.substring(startPos, endPos).trim();
    
    if (signsText) {
      // Split by | and ✓/✗ markers to get individual signs
      const signItems = [];
      
      // First split by pipe, but not pipes inside parentheses
      const pipeSegments = [];
      let current = '';
      let parenDepth = 0;
      
      for (let i = 0; i < signsText.length; i++) {
        const char = signsText[i];
        if (char === '(') parenDepth++;
        if (char === ')') parenDepth--;
        
        if (char === '|' && parenDepth === 0) {
          if (current.trim()) pipeSegments.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      if (current.trim()) pipeSegments.push(current.trim());
      
      for (const segment of pipeSegments) {
        // Then split each segment by ✓ and ✗ markers
        const splitByMarkers = segment.split(/(✓|✗)/).filter(s => s.trim());
        
        let currentMarker = null;
        let currentText = '';
        
        for (let i = 0; i < splitByMarkers.length; i++) {
          const part = splitByMarkers[i].trim();
          
          if (part === '✓' || part === '✗') {
            // Save previous sign if exists
            if (currentMarker && currentText) {
              signItems.push({
                marker: currentMarker,
                text: currentText.replace(/^[,|]\s*/, '').trim()
              });
            }
            currentMarker = part;
            currentText = '';
          } else {
            currentText += part;
          }
        }
        
        // Add the last sign
        if (currentMarker && currentText) {
          signItems.push({
            marker: currentMarker,
            text: currentText.replace(/^[,|]\s*/, '').trim()
          });
        }
      }
      
      // Convert to items with type
      const items = signItems
        .filter(s => s.text)
        .map(s => ({
          type: s.marker === '✓' ? 'positive' : 'negative',
          text: s.text
        }));
      
      // First option goes to A, second to B
      if (index === 0) {
        result.optionA.name = optionName;
        result.optionA.items = items;
      } else {
        result.optionB.name = optionName;
        result.optionB.items = items;
      }
    }
  });

  // Fallback to default names if no names found
  if (!result.optionA.name) result.optionA.name = 'Option A';
  if (!result.optionB.name) result.optionB.name = 'Option B';

  return result;
};
